/**
 * Blocks leaving Create Request / Request Detail (URL route change) when the screen
 * has unsaved work — form draft, edit overlay, or open rack editor (#1116 / #1114).
 * Step changes within the same route do not prompt; draft auto-persists locally.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

export type TryLeaveRequest = () => Promise<boolean>;
export type TryChangeRequestStep = (nextStepId: string) => Promise<boolean>;

type RequestLeaveGuardContextValue = {
  registerTryLeaveRequest: (fn: TryLeaveRequest | null) => void;
  registerTryChangeRequestStep: (fn: TryChangeRequestStep | null) => void;
  tryLeaveRequest: () => Promise<boolean>;
  tryChangeRequestStep: (nextStepId: string) => Promise<boolean>;
};

const RequestLeaveGuardContext =
  createContext<RequestLeaveGuardContextValue | null>(null);

export function RequestLeaveGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const tryLeaveRef = useRef<TryLeaveRequest | null>(null);
  const tryChangeStepRef = useRef<TryChangeRequestStep | null>(null);

  const registerTryLeaveRequest = useCallback((fn: TryLeaveRequest | null) => {
    tryLeaveRef.current = fn;
  }, []);

  const registerTryChangeRequestStep = useCallback(
    (fn: TryChangeRequestStep | null) => {
      tryChangeStepRef.current = fn;
    },
    [],
  );

  const tryLeaveRequest = useCallback(async (): Promise<boolean> => {
    if (!tryLeaveRef.current) {
      return true;
    }
    return tryLeaveRef.current();
  }, []);

  const tryChangeRequestStep = useCallback(
    async (nextStepId: string): Promise<boolean> => {
      if (!tryChangeStepRef.current) {
        return true;
      }
      return tryChangeStepRef.current(nextStepId);
    },
    [],
  );

  const value = useMemo(
    () => ({
      registerTryLeaveRequest,
      registerTryChangeRequestStep,
      tryLeaveRequest,
      tryChangeRequestStep,
    }),
    [
      registerTryLeaveRequest,
      registerTryChangeRequestStep,
      tryLeaveRequest,
      tryChangeRequestStep,
    ],
  );

  return (
    <RequestLeaveGuardContext.Provider value={value}>
      {children}
    </RequestLeaveGuardContext.Provider>
  );
}

export function useRequestLeaveGuard(): RequestLeaveGuardContextValue | null {
  return useContext(RequestLeaveGuardContext);
}

/** Register while Create Request / Request Detail is mounted; cleared on unmount. */
export function useRegisterRequestLeaveGuard(
  tryLeave: TryLeaveRequest | null,
): void {
  const guard = useRequestLeaveGuard();

  useEffect(() => {
    if (!guard) {
      return;
    }
    guard.registerTryLeaveRequest(tryLeave);
    return () => guard.registerTryLeaveRequest(null);
  }, [guard, tryLeave]);
}

/** Register step-change guard while Create Request / Request Detail is mounted. */
export function useRegisterRequestStepChangeGuard(
  tryChangeStep: TryChangeRequestStep | null,
): void {
  const guard = useRequestLeaveGuard();

  useEffect(() => {
    if (!guard) {
      return;
    }
    guard.registerTryChangeRequestStep(tryChangeStep);
    return () => guard.registerTryChangeRequestStep(null);
  }, [guard, tryChangeStep]);
}

/** @deprecated Use RequestLeaveGuardProvider */
export const RackEditorNavigationGuardProvider = RequestLeaveGuardProvider;

/** @deprecated Use useRequestLeaveGuard */
export function useRackEditorNavigationGuard(): {
  registerTryLeaveRackEditor: (fn: TryLeaveRequest | null) => void;
  tryLeaveRackEditor: () => Promise<boolean>;
} | null {
  const guard = useRequestLeaveGuard();
  if (!guard) {
    return null;
  }
  return {
    registerTryLeaveRackEditor: guard.registerTryLeaveRequest,
    tryLeaveRackEditor: guard.tryLeaveRequest,
  };
}

/** @deprecated Use useRegisterRequestLeaveGuard */
export const useRegisterRackEditorLeaveGuard = useRegisterRequestLeaveGuard;
